<template>
  <div class="sidebar" :class="{ 'sidebar--guide-open': sidebarGuideStackOpen }">
    <div class="sidebar-logo-wrap">
      <div class="sidebar-logo" @click="goToAi" style="cursor: pointer;">
        <img :src="sidebarLogo" width="64" alt="logo">
      </div>
      <Transition name="sidebar-logo-guide-pop">
        <div v-if="showLogoGuide && route.path !== '/ai'" class="sidebar-logo-guide" role="status" aria-live="polite">
          <div class="sidebar-logo-guide__arrow" aria-hidden="true" />
          <div class="sidebar-logo-guide__card">
            <p class="sidebar-logo-guide__title">点此 Logo 试试</p>
            <p class="sidebar-logo-guide__desc">打开「小奇智能体」，随时为你解答与协助操作。</p>
            <div class="sidebar-logo-guide__actions">
              <v-btn size="small" variant="flat" color="#2B57EE" class="sidebar-logo-guide__btn-main" rounded="pill"
                @click.stop="goToAi">
                去试试
              </v-btn>
              <v-btn size="small" variant="text" class="sidebar-logo-guide__btn-skip" @click.stop="dismissLogoGuide">
                知道了
              </v-btn>
            </div>
          </div>
        </div>
      </Transition>
      <Transition name="sidebar-logo-guide-pop">
        <div v-if="showAiReturnHint" class="sidebar-logo-guide" role="status" aria-live="polite">
          <div class="sidebar-logo-guide__arrow" aria-hidden="true" />
          <div class="sidebar-logo-guide__card">
            <p class="sidebar-logo-guide__title">已进入小奇智能体</p>
            <p class="sidebar-logo-guide__desc">需要回到系统时，点击左侧 Logo 即可返回工作台。</p>
            <div class="sidebar-logo-guide__actions">
              <v-btn size="small" variant="flat" color="#2B57EE" class="sidebar-logo-guide__btn-main" rounded="pill"
                @click.stop="goToAi">
                返回工作台
              </v-btn>
              <v-btn size="small" variant="text" class="sidebar-logo-guide__btn-skip" @click.stop="dismissAiReturnHint">
                知道了
              </v-btn>
            </div>
          </div>
        </div>
      </Transition>
    </div>
    <div v-if="!hideSidebarChromeForAiHint" class="sidebar-menu" ref="sidebarMenuRef">
      <template v-for="group in visibleMenuGroups" :key="group.key">
        <div class="sidebar-menu__item" :class="[
          { 'is-active': isActive(group.rootPath) },
          { 'is-hovering': megaActiveKey === group.key && megaVisible },
          `menu-item-${group.key}`
        ]" :data-menu-key="group.key" @mouseenter="onMenuItemEnter(group, $event)" @mouseleave="onMenuItemLeave"
          @click="onMenuItemClick(group)">
          <div class="sidebar-menu__inner">
            <div class="sidebar-menu__icon"></div>
            <a href="javascript:;" class="sidebar-menu__title">{{ group.title }}</a>
          </div>
        </div>
      </template>
    </div>

    <Teleport to="body">
      <MegaMenu :visible="megaVisible" :active-key="megaActiveKey" :anchor-top="megaAnchorTop"
        :anchor-left="megaAnchorLeft" :groups="visibleMenuGroups" :current-path="route.path"
        :current-query="route.query" @select="onMegaSelect" @enter="onMegaEnter" @leave="onMegaLeave"
        @close="closeMegaMenu" />
    </Teleport>

    <div v-if="!hideSidebarChromeForAiHint" class="sidebar-footer">
      <v-btn variant="flat" class="sidebar-station-pick" stacked :ripple="false" @click="() => openStationSwitch()">
        <img src="@/assets/checkoutStation.svg" class="sidebar-station-pick__img" width="66" alt="选择电站" />
      </v-btn>

      <v-btn variant="flat" class="sidebar-setting" v-if="false">
        <img src="@/assets/images/question.png" width="20" height="20" alt="setting">
      </v-btn>
    </div>

    <StationSwitchDialog ref="stationSwitchDialogRef" />
  </div>
</template>

<script setup>
import useAppStore from '@/store/modules/app'
import useSettingsStore from '@/store/modules/settings'
import usePermissionStore from '@/store/modules/permission'
import useUserStore from '@/store/modules/user'
import { useRoute, useRouter } from 'vue-router'
import { computed, ref, watch, onBeforeUnmount, onMounted } from 'vue'
import StationSwitchDialog from './StationSwitchDialog.vue'
import MegaMenu from './MegaMenu.vue'
import getMenuConfig from './megaMenuConfig'
import { hasAnyPermission } from '@/config/menuPermissionGroups'
import defaultLogo from '@/assets/logo/logo.png'


const router = useRouter()
const route = useRoute();
const appStore = useAppStore()
const userStore = useUserStore()
const settingsStore = useSettingsStore()
const permissionStore = usePermissionStore()

const stationSwitchDialogRef = ref(null)

/** 侧栏 Logo：优先使用个人中心上传的自定义 LOGO，未设置时回退到内置资源 */
const sidebarLogo = computed(() => userStore.logo || defaultLogo)

/** 侧栏 Logo 引导：首次进入未点过则展示，点击 Logo /「去试试」/「知道了」后不再出现 */
const LOGO_GUIDE_STORAGE_KEY = 'emind_sidebar_logo_xiaoqi_guide_done'
const showLogoGuide = ref((() => {
  try {
    return typeof localStorage !== 'undefined' && !localStorage.getItem(LOGO_GUIDE_STORAGE_KEY)
  } catch {
    return false
  }
})())

function dismissLogoGuide() {
  showLogoGuide.value = false
  try {
    localStorage.setItem(LOGO_GUIDE_STORAGE_KEY, '1')
  } catch {
    /* ignore quota / private mode */
  }
}

/** 在 AI 页提示「点击 Logo 返回」；点 Logo /「返回工作台」/「知道了」后不再出现 */
const AI_RETURN_HINT_STORAGE_KEY = 'emind_ai_page_logo_return_hint_done'
const showAiReturnHint = ref(false)

function dismissAiReturnHint() {
  showAiReturnHint.value = false
  try {
    localStorage.setItem(AI_RETURN_HINT_STORAGE_KEY, '1')
  } catch {
    /* ignore */
  }
}

watch(
  () => route.path,
  (path) => {
    if (path !== '/ai') {
      showAiReturnHint.value = false
      return
    }
    try {
      showAiReturnHint.value = !localStorage.getItem(AI_RETURN_HINT_STORAGE_KEY)
    } catch {
      showAiReturnHint.value = true
    }
  },
  { immediate: true }
)

const sidebarGuideStackOpen = computed(
  () => (showLogoGuide.value && route.path !== '/ai') || showAiReturnHint.value
)

/** AI 返回提示抬高侧栏 z-index 时，不渲染中间菜单与底部，避免叠在全屏智能体上 */
const hideSidebarChromeForAiHint = computed(
  () => route.path === '/ai' && showAiReturnHint.value
)

function openStationSwitch(...args) {
  if (stationSwitchDialogRef.value) {
    stationSwitchDialogRef.value.open(...args)
  }
}

/** 供 Layout ref 调用，与运营页 inject 的 openStationSwitch 一致 */
defineExpose({
  openStationSwitch
})

// 定义事件
const emit = defineEmits(['toggle-chat-box'])

const isStationMonitor = computed(() => route.name === 'StationMonitor')
const currentStationId = computed(() => route.query.stationId || '')

function goToAi() {
  dismissLogoGuide()
  const currentPath = route.path
  if (currentPath === '/ai') {
    dismissAiReturnHint()
    router.push({ path: '/index' })
  } else {
    router.push({ path: '/ai' })
  }
}

import { constantRoutes } from '@/router'

function getSidebarRouters(rootNodes = []) {
  for (const route of rootNodes) {
    if (route.name === 'Layout') {
      return filterSidebar(route.children)
    }
  }
}

/**
 * 过滤掉不需要出现在侧边栏的路由
 * @param list 列表
 */
function filterSidebar(list) {
  const res = []
  list.forEach(item => {
    if (item.meta?.sidebar) {
      if (Array.isArray(item.children)) {
        item.children = filterSidebar(item.children)
      }
      res.push(item)
    }
  })
  return res
}

function isActive(path) {
  const paths = new Set(route.matched.map(item => item.path))
  return paths.has(path)
}

function onMenuItemClick(group) {
  const target = resolveFirstAllowedTarget(group)
  router.push({
    path: target.path,
    query: target.query && Object.keys(target.query).length ? target.query : undefined,
  })
  openMegaMenu(group)
}

function resolveFirstAllowedTarget(group) {
  const firstSection = group.sections?.[0]
  let path = group.rootPath
  let query
  if (firstSection) {
    const canAccessSection = hasAnyPermission(userStore.permissions, firstSection.perms || [])
    const item = canAccessSection
      ? firstSection
      : (firstSection.children?.[0] || firstSection)
    path = item.path || group.rootPath
    query = item.query ? { ...item.query } : undefined
  }
  const needStationId = [
    '/station-monitor/detail',
    '/business-operations/detail',
    '/it-operations/detail',
  ].includes(path)
  if (needStationId) {
    query = query || {}
    if (!query.stationId) {
      query.stationId = appStore.stationId || appStore.stations?.[0]?.stationId
    }
  }
  return { path, query }
}


/* ============ MegaMenu 浮框 ============ */
const { visibleMenuGroups } = getMenuConfig()//megaMenuConfig
const megaVisible = ref(false)
const megaActiveKey = ref('')
const megaAnchorTop = ref(0)
const megaAnchorLeft = ref(84)
let leaveTimer = null

function clearLeaveTimer() {
  if (leaveTimer) {
    clearTimeout(leaveTimer)
    leaveTimer = null
  }
}

function openMegaMenu(group, el) {
  // 正常情况下无二级菜单的一级菜单已被过滤，这里保留兜底处理
  if (!group.hover || !group.sections?.length) {
    closeMegaMenu()
    return
  }
  clearLeaveTimer()
  megaActiveKey.value = group.key
  // 三角对齐：用当前 hover 的一级图标视口中心
  const target = el || document.querySelector(`.sidebar-menu__item[data-menu-key="${group.key}"]`)
  if (target) {
    const rect = target.getBoundingClientRect()
    megaAnchorTop.value = rect.top + rect.height / 2
    // 浮框 left：侧栏右沿 + 留白
    if (!megaVisible.value) {
      megaAnchorLeft.value = rect.right + 8
    }
  }
  megaVisible.value = true
}

function closeMegaMenu() {
  clearLeaveTimer()
  megaVisible.value = false
  megaActiveKey.value = ''
}

function onMenuItemEnter(group, e) {
  openMegaMenu(group, e.currentTarget)
}

function onMenuItemLeave() {
  // 离开图标后短暂延时，给鼠标横向移动到浮框留出时间
  clearLeaveTimer()
  leaveTimer = setTimeout(() => {
    closeMegaMenu()
  }, 200)
}

function onMegaEnter() {
  clearLeaveTimer()
}

function onMegaLeave() {
  clearLeaveTimer()
  leaveTimer = setTimeout(() => {
    closeMegaMenu()
  }, 150)
}

function onMegaSelect(item) {
  closeMegaMenu()
  const query = item.query ? { ...item.query } : {}
  // 需要 stationId 的二级菜单（站级监控 / 运营详情 / 数据分析子项 等）：
  // 如果当前 appStore 有选中电站，自动带上，避免目标页空白
  const needStationId = ['/station-monitor/detail', '/business-operations/detail', '/it-operations/detail'].includes(item.path)
  if (needStationId && !query.stationId) {
    query.stationId = appStore.stationId || appStore.stations?.[0]?.stationId
  }
  router.push({ path: item.path, query: Object.keys(query).length ? query : undefined })
}

// 路由变化时主动关掉浮框，避免遮挡新页面
watch(() => route.fullPath, () => {
  closeMegaMenu()
})

onBeforeUnmount(() => {
  clearLeaveTimer()
})
</script>

<style scoped lang="scss">
.sidebar {
  width: var(--sidebar-width);
  display: flex;
  flex-direction: column;
  align-items: center;

  &--guide-open {
    position: relative;
    z-index: 100;
  }
}

.sidebar-logo-wrap {
  position: relative;
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 50;
}

.sidebar-logo {
  // width: 66px;
  // height: 66px;
  // background: #3A68FF;
  // border-radius: 24px; // 更新圆角
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  animation: sidebar-logo-float 3.5s ease-in-out infinite;

  &--guide {
    border-radius: 18px;
    animation:
      sidebar-logo-float 3.5s ease-in-out infinite,
      sidebar-logo-guide-ring 1.6s ease-in-out infinite;
  }

  // 背后一层柔和色光，随呼吸变化
  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 76px;
    height: 76px;
    border-radius: 22px;
    pointer-events: none;
    z-index: 0;
    background: radial-gradient(65% 65% at 50% 45%,
        rgba(38, 85, 241, 0.42) 0%,
        rgba(120, 150, 255, 0.12) 45%,
        transparent 72%);
    filter: blur(10px);
    transform: translate(calc(-50% - 2px), -50%) scale(0.92);
    animation: sidebar-logo-ambient 3.2s ease-in-out infinite;
  }

  // 斜向扫过高光（与 blend 叠加在 logo 上）
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: 68px;
    height: 68px;
    transform: translate(calc(-50% - 2px), -50%);
    border-radius: 18px;
    pointer-events: none;
    z-index: 2;
    mix-blend-mode: soft-light;
    opacity: 0;
    background: linear-gradient(118deg,
        transparent 0%,
        transparent 40%,
        rgba(255, 255, 255, 0.55) 50%,
        transparent 60%,
        transparent 100%);
    background-size: 260% 100%;
    background-position: 130% 0;
    animation: sidebar-logo-glint 5s ease-in-out infinite;
  }

  >img {
    position: relative;
    left: -4px;
    display: block;
    z-index: 1;
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.35s ease;
    transform-origin: center center;
    animation: sidebar-logo-glow 2.8s ease-in-out infinite;
  }

  &:hover>img {
    transform: scale(1.08);
    animation: none;
    filter: brightness(1.06) drop-shadow(0 6px 18px rgba(38, 85, 241, 0.45)) drop-shadow(0 0 28px rgba(38, 85, 241, 0.35)) drop-shadow(0 0 20px rgba(241, 91, 255, 0.18));
  }

  &:active>img {
    transform: scale(1.02);
  }
}

@keyframes sidebar-logo-float {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-4px);
  }
}

@keyframes sidebar-logo-ambient {

  0%,
  100% {
    opacity: 0.55;
    transform: translate(calc(-50% - 2px), -50%) scale(0.92);
  }

  50% {
    opacity: 0.95;
    transform: translate(calc(-50% - 2px), -50%) scale(1.08);
  }
}

@keyframes sidebar-logo-glow {

  0%,
  100% {
    filter: drop-shadow(0 4px 12px rgba(38, 85, 241, 0.28)) drop-shadow(0 0 18px rgba(38, 85, 241, 0.18));
  }

  50% {
    filter: drop-shadow(0 8px 22px rgba(38, 85, 241, 0.42)) drop-shadow(0 0 26px rgba(38, 85, 241, 0.28)) drop-shadow(0 0 14px rgba(255, 255, 255, 0.12));
  }
}

@keyframes sidebar-logo-glint {

  0%,
  52% {
    opacity: 0;
    background-position: 130% 0;
  }

  58% {
    opacity: 0.85;
  }

  72% {
    opacity: 0.85;
    background-position: -30% 0;
  }

  82%,
  100% {
    opacity: 0;
    background-position: -30% 0;
  }
}

@keyframes sidebar-logo-guide-ring {

  0%,
  100% {
    outline: 2px solid rgba(38, 85, 241, 0.55);
    outline-offset: 2px;
  }

  50% {
    outline: 2px solid rgba(130, 110, 255, 0.85);
    outline-offset: 7px;
  }
}

.sidebar-logo-guide {
  position: absolute;
  left: calc(100% + 10px);
  top: 69%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  z-index: 20;
  pointer-events: auto;
  filter: drop-shadow(0 8px 24px rgba(38, 85, 241, 0.22));
}

.sidebar-logo-guide__arrow {
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 8px solid #fff;
  flex-shrink: 0;
  margin-right: -1px;
  position: relative;
  top: -14px;
}

.sidebar-logo-guide__card {
  min-width: 200px;
  max-width: 236px;
  padding: 12px 14px 10px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid rgba(38, 85, 241, 0.12);
}

.sidebar-logo-guide__title {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: #2e2e2e;
  line-height: 1.35;
}

.sidebar-logo-guide__desc {
  margin: 0 0 10px;
  font-size: 12px;
  color: #6b6c6d;
  line-height: 1.45;
}

.sidebar-logo-guide__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
}

.sidebar-logo-guide__btn-main {
  font-weight: 500;
  letter-spacing: 0.02em;
}

.sidebar-logo-guide__btn-skip {
  color: #6b6c6d !important;
  min-width: 0 !important;
  padding: 0 6px !important;
}

.sidebar-logo-guide-pop-enter-active,
.sidebar-logo-guide-pop-leave-active {
  transition: opacity 0.28s ease;
}

.sidebar-logo-guide-pop-enter-from,
.sidebar-logo-guide-pop-leave-to {
  opacity: 0;
}

.sidebar-menu {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px; // 菜单项之间的间距，也是连接线的高度

  --size: 66px;
  --radius: 20px;

  .sidebar-menu__item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    width: var(--size);
    height: var(--size);
    border-radius: var(--radius);
    background: #fff; // 白色底框
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.05);
    z-index: 1;

    // 连接线
    &:not(:last-child)::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      width: 30px; // 连接线宽度
      height: 12px; // 连接线高度，对应 gap
      background: #fff;
      z-index: -1;
    }

    &:hover {
      .sidebar-menu__title {
        color: #2E2E2E;
      }

      .sidebar-menu__icon {
        background: #2E2E2E;
      }
    }

    &.is-hovering:not(.is-active) {
      .sidebar-menu__inner {
        background: rgba(38, 85, 241, 0.08);
        border-radius: 15px;
      }

      .sidebar-menu__title {
        color: #2655F1;
      }

      .sidebar-menu__icon {
        background: #2655F1;
      }
    }

    &.is-active {
      // 选中时背景保持白色，内部显示黑色块
      background: #fff;

      .sidebar-menu__inner {
        background: #2655F1;
        width: 62px;
        height: 62px;
        border-radius: 15px;
        box-shadow: 0 4px 12px 0 rgba(38, 85, 241, 0.30);
      }

      .sidebar-menu__title {
        color: #fff;
        font-weight: 500;
      }

      .sidebar-menu__icon {
        background: #fff;
      }
    }
  }

  .sidebar-menu__inner {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border-radius: var(--radius);
    transition: all 0.3s ease;
  }

  .sidebar-menu__icon {
    width: 24px;
    height: 24px;
    background: #6B6C6D; // 默认灰色图标
    transition: background-color 0.3s;
    // 使用mask可以让背景色填充svg形状
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;

    // 这里定义默认图标（工作台）
    // mask-image: url("..."); // 已移除默认，改用类名控制
  }

  // 针对不同菜单项设置不同图标 (根据索引)
  // 工作台
  .menu-item-dashboard {
    .sidebar-menu__icon {
      mask-image: url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14 2.59644C14.367 2.59649 14.7287 2.68672 15.0527 2.85913L23.8223 7.52612C24.0769 7.66172 24.2898 7.86382 24.4385 8.11108C24.55 8.2967 24.6222 8.50248 24.6514 8.71558L24.666 8.93042V19.0916C24.6659 19.6831 24.3397 20.2245 23.8203 20.4988H23.8193L15.0479 25.1462C14.3923 25.4937 13.6067 25.4937 12.9512 25.1462V25.1453L4.17969 20.5017C3.92396 20.3662 3.70977 20.1627 3.56055 19.9148C3.4115 19.6669 3.33295 19.3827 3.33301 19.0935V8.93237C3.33301 8.34247 3.65853 7.8006 4.17773 7.52417L4.17676 7.52319L12.9473 2.85913C13.2713 2.68675 13.633 2.59644 14 2.59644Z' stroke='currentColor' stroke-width='2'/%3E%3Cpath d='M18.8953 10.0193C19.028 9.94152 19.1748 9.89073 19.3273 9.86989C19.4797 9.84904 19.6348 9.85856 19.7835 9.89788C19.9323 9.93721 20.0718 10.0056 20.194 10.099C20.3162 10.1925 20.4188 10.3092 20.4957 10.4425C20.5726 10.5757 20.6224 10.7228 20.6423 10.8754C20.6621 11.028 20.6516 11.183 20.6112 11.3315C20.5709 11.48 20.5016 11.619 20.4074 11.7406C20.3131 11.8622 20.1957 11.964 20.062 12.04L15.1666 14.8657V19.8333C15.1666 20.1428 15.0437 20.4395 14.8249 20.6583C14.6061 20.8771 14.3094 21 14 21C13.6905 21 13.3938 20.8771 13.175 20.6583C12.9562 20.4395 12.8333 20.1428 12.8333 19.8333V14.8668L7.93796 12.04C7.67219 11.8842 7.47883 11.6296 7.40009 11.3317C7.32134 11.0339 7.3636 10.717 7.51764 10.4502C7.67169 10.1834 7.92501 9.98835 8.22232 9.90763C8.51964 9.82691 8.83681 9.86706 9.10463 10.0193L13.9813 12.8333H14.0163L18.8953 10.0193Z' fill='currentColor'/%3E%3C/svg%3E");
    }

    &.is-active .sidebar-menu__icon {
      background: transparent;
      mask-image: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28' fill='none'%3E%3Cpath d='M15.5212 1.9763L24.291 6.64297C24.7059 6.86376 25.0529 7.19316 25.2949 7.59594C25.537 7.99871 25.6651 8.45971 25.6654 8.92964V19.0913C25.6654 20.0526 25.1345 20.9346 24.2864 21.3826L15.5154 26.0295C14.5669 26.5323 13.4305 26.5323 12.482 26.0295L3.71103 21.385C3.29464 21.1644 2.94623 20.8346 2.70326 20.4309C2.46028 20.0271 2.33194 19.5648 2.33203 19.0936V8.93197C2.33203 7.97297 2.86053 7.09214 3.70636 6.6418L12.4762 1.9763C12.9449 1.72688 13.4677 1.59644 13.9987 1.59644C14.5297 1.59644 15.0525 1.72688 15.5212 1.9763Z' fill='white'/%3E%3Cpath d='M18.8933 10.0193C19.0261 9.94152 19.1729 9.89073 19.3253 9.86989C19.4778 9.84904 19.6328 9.85856 19.7816 9.89788C19.9303 9.93721 20.0698 10.0056 20.1921 10.099C20.3143 10.1925 20.4168 10.3092 20.4937 10.4425C20.5707 10.5757 20.6205 10.7228 20.6403 10.8754C20.6601 11.028 20.6496 11.183 20.6093 11.3315C20.569 11.48 20.4997 11.619 20.4054 11.7406C20.3112 11.8622 20.1938 11.964 20.06 12.04L15.1647 14.8657V19.8333C15.1647 20.1428 15.0418 20.4395 14.823 20.6583C14.6042 20.8771 14.3074 21 13.998 21C13.6886 21 13.3918 20.8771 13.173 20.6583C12.9543 20.4395 12.8313 20.1428 12.8313 19.8333V14.8668L7.93601 12.04C7.67023 11.8842 7.47688 11.6296 7.39813 11.3317C7.31939 11.0339 7.36165 10.717 7.51569 10.4502C7.66973 10.1834 7.92305 9.98835 8.22037 9.90763C8.51768 9.82691 8.83681 9.86706 9.10268 10.0193L13.9793 12.8333H14.0143L18.8933 10.0193Z' fill='%232E2E2E'/%3E%3C/svg%3E");
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
    }
  }

  // 告警管理
  .menu-item-stations {
    .sidebar-menu__icon {
      mask-image: url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.999 7.44141C18.3691 7.44141 21.9348 10.8569 22.0498 15.1826V23.4736H24.3496C25.0395 23.4736 25.4999 23.9285 25.5 24.6113C25.5 25.1804 25.0397 25.6361 24.4648 25.75H3.64941C2.9594 25.75 2.49902 25.2943 2.49902 24.6113C2.4992 24.0423 2.9593 23.5875 3.53418 23.4736H5.94922V15.4102C5.94922 11.0845 9.39945 7.55524 13.7695 7.44141H13.999ZM13.999 9.71875C10.8941 9.71893 8.36402 12.1092 8.24902 15.1826V23.4736H19.749V15.4102C19.749 12.4505 17.449 9.94642 14.459 9.71875H13.999ZM14.001 14.7656C14.5809 14.7656 15.0508 15.2355 15.0508 15.8154V21.0332C15.0504 21.6128 14.5807 22.083 14.001 22.083C13.4213 22.083 12.9515 21.6128 12.9512 21.0332V15.8154C12.9512 15.2355 13.4211 14.7656 14.001 14.7656ZM3.53418 5.73438C3.99419 5.27904 4.68452 5.27862 5.14453 5.62012L6.86914 7.32812C7.32907 7.78346 7.32912 8.46657 6.86914 8.92188C6.40917 9.377 5.83396 9.37661 5.37402 9.03516L5.25879 8.92188L3.53418 7.32812C3.0742 6.87282 3.07426 6.18971 3.53418 5.73438ZM22.8545 5.62012C23.3145 5.27867 24.0046 5.27906 24.3496 5.73438C24.8094 6.18962 24.8097 6.7586 24.4648 7.21387L24.3496 7.32812L22.7393 8.92188L22.625 9.03516C22.1651 9.37656 21.475 9.37683 21.1299 8.92188C20.6699 8.46654 20.6696 7.89674 21.0146 7.44141L21.1299 7.32812L22.8545 5.62012ZM13.999 1.75C14.574 1.75 15.0344 2.20528 15.1494 2.77441V5.16504C15.1494 5.84803 14.689 6.30371 13.999 6.30371C13.4243 6.30352 12.9647 5.84813 12.8496 5.2793V2.88867C12.8496 2.20579 13.3092 1.75016 13.999 1.75Z' fill='currentColor'/%3E%3C/svg%3E");
    }

    &.is-active .sidebar-menu__icon {
      background: transparent;
      mask-image: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28' fill='none'%3E%3Cpath d='M14 7.44141C18.3701 7.44141 21.9358 10.8569 22.0508 15.1826V23.4736H24.3506C25.0404 23.4737 25.4999 23.9286 25.5 24.6113C25.5 25.1804 25.0407 25.636 24.4658 25.75H3.65039C2.96038 25.75 2.5 25.2943 2.5 24.6113C2.50018 24.0423 2.96026 23.5874 3.53516 23.4736H5.9502V15.4102C5.9502 11.0845 9.40042 7.55524 13.7705 7.44141H14ZM3.53516 5.73438C3.99517 5.27904 4.6855 5.27862 5.14551 5.62012L6.87012 7.32812C7.32998 7.78346 7.33008 8.46659 6.87012 8.92188C6.41013 9.37706 5.83496 9.37662 5.375 9.03516L5.25977 8.92188L3.53516 7.32812C3.07516 6.8728 3.07517 6.18971 3.53516 5.73438ZM22.8555 5.62012C23.3155 5.27873 24.0056 5.27909 24.3506 5.73438C24.8103 6.18959 24.8106 6.75863 24.4658 7.21387L24.3506 7.32812L22.7402 8.92188L22.625 9.03516C22.1651 9.37643 21.4759 9.37672 21.1309 8.92188C20.6709 8.46654 20.6706 7.89674 21.0156 7.44141L21.1309 7.32812L22.8555 5.62012ZM14 1.75C14.575 1.75 15.0354 2.20527 15.1504 2.77441V5.16504C15.1504 5.84803 14.69 6.30371 14 6.30371C13.4253 6.30359 12.9657 5.84816 12.8506 5.2793V2.88867C12.8506 2.20575 13.3101 1.7501 14 1.75Z' fill='white'/%3E%3Cpath d='M14 15.8152V21.0326' stroke='%232E2E2E' stroke-width='2.1' stroke-linecap='round'/%3E%3C/svg%3E");
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
    }
  }

  // // 实时监控
  // .menu-item-monitor {
  //   .sidebar-menu__icon {
  //     mask-image: url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14 1.9502C14.5799 1.9502 15.0498 2.4201 15.0498 3V3.9502H22.75C24.4345 3.9502 25.7998 5.31553 25.7998 7V21C25.7998 22.6845 24.4345 24.0498 22.75 24.0498H15.0498V25C15.0498 25.5799 14.5799 26.0498 14 26.0498C13.4201 26.0498 12.9502 25.5799 12.9502 25V24.0498H5.25C3.56553 24.0498 2.2002 22.6845 2.2002 21V7C2.2002 5.31553 3.56553 3.9502 5.25 3.9502H12.9502V3C12.9502 2.4201 13.4201 1.9502 14 1.9502ZM5.25 6.0498C4.72533 6.0498 4.2998 6.47533 4.2998 7V21C4.2998 21.5247 4.72533 21.9502 5.25 21.9502H22.75C23.2747 21.9502 23.7002 21.5247 23.7002 21V7C23.7002 6.47533 23.2747 6.0498 22.75 6.0498H5.25ZM19.2148 10.3027C19.6 9.86943 20.2638 9.82993 20.6973 10.2148C21.1306 10.6 21.1701 11.2638 20.7852 11.6973L18.0078 14.8223C16.954 16.0079 15.1665 16.1881 13.8975 15.2363L12.8535 14.4531C12.4565 14.1554 11.8956 14.2128 11.5654 14.5879C10.4234 15.8857 9.05966 17.4247 8.74219 17.7422C8.33214 18.1522 7.66786 18.1522 7.25781 17.7422C6.84776 17.3321 6.84776 16.6679 7.25781 16.2578C7.5134 16.0022 8.80977 14.5395 9.98828 13.2002C11.0404 12.0046 12.8375 11.8149 14.1143 12.7725L15.1582 13.5557C15.5534 13.852 16.1093 13.796 16.4375 13.4268L19.2148 10.3027Z' fill='currentColor'/%3E%3C/svg%3E");
  //   }

  //   &.is-active .sidebar-menu__icon {
  //     background: transparent;
  //     mask-image: none;
  //     background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28' fill='none'%3E%3Cpath d='M13.999 1.9502C14.5789 1.9502 15.0488 2.4201 15.0488 3V3.9502H22.749C24.4335 3.9502 25.7988 5.31553 25.7988 7V21C25.7988 22.6845 24.4335 24.0498 22.749 24.0498H15.0488V25C15.0488 25.5799 14.5789 26.0498 13.999 26.0498C13.4191 26.0498 12.9492 25.5799 12.9492 25V24.0498H5.24902C3.56455 24.0498 2.19922 22.6845 2.19922 21V7C2.19922 5.31553 3.56456 3.9502 5.24902 3.9502H12.9492V3C12.9492 2.4201 13.4191 1.9502 13.999 1.9502ZM5.24902 6.0498C4.72435 6.0498 4.29883 6.47533 4.29883 7V21C4.29883 21.5247 4.72435 21.9502 5.24902 21.9502H22.749C23.2737 21.9502 23.6992 21.5247 23.6992 21V7C23.6992 6.47533 23.2737 6.0498 22.749 6.0498H5.24902Z' fill='white'/%3E%3Crect x='4' y='5' width='20' height='18' fill='white'/%3E%3Cpath d='M8 17C8.28653 16.7135 9.6164 15.2126 10.7767 13.894C11.4678 13.1086 12.6468 12.9851 13.4838 13.6128L14.5278 14.3958C15.3599 15.0199 16.5315 14.902 17.2226 14.1246L20 11' stroke='%232E2E2E' stroke-width='2.1' stroke-linecap='round'/%3E%3C/svg%3E");
  //     background-size: contain;
  //     background-repeat: no-repeat;
  //     background-position: center;
  //   }
  // }
  // 实时监控
  .menu-item-monitor {
    .sidebar-menu__icon {
      mask-image: url("data:image/svg+xml,%3Csvg width='23' height='23' viewBox='0 0 23 23' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11.5 1.82955C16.8318 1.82955 21.1705 6.16818 21.1705 11.5C21.1705 16.8318 16.8318 21.1705 11.5 21.1705C6.16818 21.1705 1.82955 16.8318 1.82955 11.5C1.82955 6.16818 6.16818 1.82955 11.5 1.82955ZM11.5 0C5.12273 0 0 5.12273 0 11.5C0 17.8773 5.175 23 11.5 23C17.825 23 23 17.825 23 11.5C23 5.175 17.8773 0 11.5 0Z' fill='%236B6C6D'/%3E%3Cpath d='M13.7993 18.4505H13.747C13.3811 18.3982 13.1197 18.1369 13.0152 17.8232L11.2902 8.72777L10.3493 12.021C10.2447 12.3346 9.93111 12.5437 9.61747 12.5437H8.67656L7.73565 14.896C7.63111 15.2096 7.31747 15.4187 7.00384 15.4187C6.6902 15.4187 6.37656 15.2096 6.27202 14.896L5.12202 11.446L4.75611 12.1778C4.65156 12.4391 4.33793 12.6482 4.02429 12.6482H3.13565C2.71747 12.6482 2.35156 12.2823 2.35156 11.8641C2.35156 11.446 2.71747 10.9755 3.13565 10.9755H3.55384L4.54702 8.88459C4.70384 8.57095 4.9652 8.41413 5.33111 8.41413C5.64474 8.41413 5.90611 8.62322 6.01065 8.93686L7.05611 12.1778L7.36974 11.3937C7.47429 11.08 7.78793 10.871 8.10156 10.871H8.9902L10.7152 5.01641C10.8197 4.6505 11.1334 4.44141 11.4993 4.44141C11.8652 4.44141 12.1788 4.70277 12.2311 5.06868L14.0607 14.6346L16.4129 7.99595C16.5175 7.68232 16.8311 7.47322 17.1447 7.47322C17.4584 7.47322 17.772 7.68232 17.8766 7.99595L18.922 10.9755H19.8629C20.2811 10.9755 20.647 11.2891 20.647 11.7596C20.647 12.1778 20.3334 12.5437 19.8629 12.5437H18.347C17.9811 12.5437 17.7197 12.3346 17.6152 12.021L17.0925 10.6096L14.5311 17.9278C14.4266 18.2414 14.1129 18.4505 13.7993 18.4505Z' fill='%236B6C6D'/%3E%3C/svg%3E")
    }

    // &.is-active .sidebar-menu__icon {
    //   background: transparent;
    //   mask-image: none;
    //   background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28' fill='none'%3E%3Cpath d='M13.999 1.9502C14.5789 1.9502 15.0488 2.4201 15.0488 3V3.9502H22.749C24.4335 3.9502 25.7988 5.31553 25.7988 7V21C25.7988 22.6845 24.4335 24.0498 22.749 24.0498H15.0488V25C15.0488 25.5799 14.5789 26.0498 13.999 26.0498C13.4191 26.0498 12.9492 25.5799 12.9492 25V24.0498H5.24902C3.56455 24.0498 2.19922 22.6845 2.19922 21V7C2.19922 5.31553 3.56456 3.9502 5.24902 3.9502H12.9492V3C12.9492 2.4201 13.4191 1.9502 13.999 1.9502ZM5.24902 6.0498C4.72435 6.0498 4.29883 6.47533 4.29883 7V21C4.29883 21.5247 4.72435 21.9502 5.24902 21.9502H22.749C23.2737 21.9502 23.6992 21.5247 23.6992 21V7C23.6992 6.47533 23.2737 6.0498 22.749 6.0498H5.24902Z' fill='white'/%3E%3Crect x='4' y='5' width='20' height='18' fill='white'/%3E%3Cpath d='M8 17C8.28653 16.7135 9.6164 15.2126 10.7767 13.894C11.4678 13.1086 12.6468 12.9851 13.4838 13.6128L14.5278 14.3958C15.3599 15.0199 16.5315 14.902 17.2226 14.1246L20 11' stroke='%232E2E2E' stroke-width='2.1' stroke-linecap='round'/%3E%3C/svg%3E");
    //   background-size: contain;
    //   background-repeat: no-repeat;
    //   background-position: center;
    // }
  }

  // 运营管理
  .menu-item-business {
    .sidebar-menu__icon {
      mask-image: url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14 1.9502C14.5799 1.9502 15.0498 2.4201 15.0498 3V3.9502H22.75C24.4345 3.9502 25.7998 5.31553 25.7998 7V21C25.7998 22.6845 24.4345 24.0498 22.75 24.0498H15.0498V25C15.0498 25.5799 14.5799 26.0498 14 26.0498C13.4201 26.0498 12.9502 25.5799 12.9502 25V24.0498H5.25C3.56553 24.0498 2.2002 22.6845 2.2002 21V7C2.2002 5.31553 3.56553 3.9502 5.25 3.9502H12.9502V3C12.9502 2.4201 13.4201 1.9502 14 1.9502ZM5.25 6.0498C4.72533 6.0498 4.2998 6.47533 4.2998 7V21C4.2998 21.5247 4.72533 21.9502 5.25 21.9502H22.75C23.2747 21.9502 23.7002 21.5247 23.7002 21V7C23.7002 6.47533 23.2747 6.0498 22.75 6.0498H5.25ZM19.2148 10.3027C19.6 9.86943 20.2638 9.82993 20.6973 10.2148C21.1306 10.6 21.1701 11.2638 20.7852 11.6973L18.0078 14.8223C16.954 16.0079 15.1665 16.1881 13.8975 15.2363L12.8535 14.4531C12.4565 14.1554 11.8956 14.2128 11.5654 14.5879C10.4234 15.8857 9.05966 17.4247 8.74219 17.7422C8.33214 18.1522 7.66786 18.1522 7.25781 17.7422C6.84776 17.3321 6.84776 16.6679 7.25781 16.2578C7.5134 16.0022 8.80977 14.5395 9.98828 13.2002C11.0404 12.0046 12.8375 11.8149 14.1143 12.7725L15.1582 13.5557C15.5534 13.852 16.1093 13.796 16.4375 13.4268L19.2148 10.3027Z' fill='currentColor'/%3E%3C/svg%3E");
    }
  }

  // 运维管理
  .menu-item-it {
    .sidebar-menu__icon {
      mask-image: url("data:image/svg+xml,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.85159 5.35693C5.03314 6.63749 3.66887 8.51968 3.04822 10.7141M18.1483 5.35693C19.9668 6.63749 21.331 8.51968 21.9517 10.7141M18.7499 20.9819C16.9956 22.6178 15.0879 23.2141 12.4999 23.2141C10.711 23.2141 9.03383 22.7358 7.58923 21.9001' stroke='%236B6C6D' stroke-width='2'/%3E%3Ccircle cx='12.9464' cy='4.01786' r='3.01786' stroke='%236B6C6D' stroke-width='2'/%3E%3Ccircle cx='4.01786' cy='16.5179' r='3.01786' stroke='%236B6C6D' stroke-width='2'/%3E%3Ccircle cx='20.9821' cy='15.6248' r='3.01786' stroke='%236B6C6D' stroke-width='2'/%3E%3C/svg%3E")
    }
  }


  // 系统设置
  .menu-item-system {
    .sidebar-menu__icon {
      mask-image: url("data:image/svg+xml,%3Csvg width='26' height='24' viewBox='0 0 26 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16.1255 0C17.1495 2.60105e-05 18.1554 0.269575 19.0421 0.781556C19.9289 1.29354 20.6652 2.02991 21.1772 2.91667L24.6084 8.85908C25.1203 9.74586 25.3899 10.7518 25.3899 11.7758C25.3899 12.7997 25.1203 13.8056 24.6084 14.6924L21.1766 20.6348C20.6646 21.5216 19.9283 22.258 19.0415 22.7699C18.1548 23.2819 17.1489 23.5515 16.1249 23.5515H9.26494C8.241 23.5515 7.2351 23.2819 6.34835 22.7699C5.4616 22.258 4.72524 21.5216 4.21327 20.6348L0.781519 14.6924C0.269537 13.8056 0 12.7997 0 11.7758C0 10.7518 0.269537 9.74586 0.781519 8.85908L4.21327 2.91667C4.72524 2.02991 5.4616 1.29354 6.34835 0.781556C7.2351 0.269575 8.241 2.60105e-05 9.26494 0H16.1255ZM16.1255 2.33333H9.26494C8.65057 2.33335 8.04704 2.49508 7.51499 2.80227C6.98294 3.10945 6.54112 3.55128 6.23394 4.08333L2.8016 10.0258C2.49441 10.5578 2.33269 11.1614 2.33269 11.7758C2.33269 12.3901 2.49441 12.9937 2.8016 13.5258L6.23277 19.4682C6.53995 20.0002 6.98177 20.442 7.51382 20.7492C8.04587 21.0564 8.64941 21.2182 9.26377 21.2182H16.1249C16.7393 21.2182 17.3428 21.0564 17.8749 20.7492C18.4069 20.442 18.8488 20.0002 19.1559 19.4682L22.5883 13.5258C22.8955 12.9937 23.0572 12.3901 23.0572 11.7758C23.0572 11.1614 22.8955 10.5578 22.5883 10.0258L19.1571 4.08333C18.8499 3.55128 18.4081 3.10945 17.8761 2.80227C17.344 2.49508 16.7405 2.33335 16.1261 2.33333H16.1255ZM12.9096 7.69242C13.7502 7.69242 14.3831 7.82892 14.9682 8.14158C15.5472 8.44935 16.021 8.9233 16.3285 9.5025C16.6418 10.0876 16.7783 10.7205 16.7783 11.5611V11.9904C16.7783 12.831 16.6418 13.4639 16.3291 14.049C16.0213 14.6281 15.5474 15.1018 14.9682 15.4093C14.3831 15.7226 13.7502 15.8591 12.9096 15.8591H12.4803C11.6397 15.8591 11.0068 15.7226 10.4217 15.4099C9.84262 15.1022 9.36887 14.6282 9.06135 14.049C8.7481 13.4639 8.6116 12.831 8.6116 11.9904V11.5611C8.6116 10.7205 8.7481 10.0876 9.06077 9.5025C9.36853 8.92344 9.84249 8.44968 10.4217 8.14217C11.0068 7.82892 11.6397 7.69242 12.4803 7.69242H12.9096ZM12.9096 10.0258H12.4803C12.0014 10.0258 11.7424 10.0817 11.5224 10.1996C11.3451 10.2941 11.2133 10.4259 11.1188 10.6033C11.0009 10.8232 10.9449 11.0816 10.9449 11.5611V11.9904C10.9449 12.4693 11.0009 12.7283 11.1188 12.9482C11.2133 13.1256 11.3451 13.2574 11.5224 13.3519C11.7424 13.4697 12.0008 13.5258 12.4803 13.5258H12.9096C13.3885 13.5258 13.6475 13.4697 13.8674 13.3519C14.0448 13.2574 14.1766 13.1256 14.2711 12.9482C14.3889 12.7283 14.4449 12.4699 14.4449 11.9904V11.5611C14.4449 11.0822 14.3889 10.8232 14.2711 10.6033C14.1809 10.4306 14.04 10.2898 13.8674 10.1996C13.6475 10.0817 13.3891 10.0258 12.9096 10.0258Z' fill='%236B6C6D'/%3E%3C/svg%3E")
    }
  }

  .sidebar-menu__title {
    color: #6B6C6D;
    text-decoration: none;
    font-size: 10px; // 设计稿字体大小
    transition: color 0.3s ease-in-out;
    font-family: 'HarmonyOS Sans SC', sans-serif;
  }

  // AI图标特殊样式 - 只显示图片，无背景
  .ai-icon-item {
    position: relative;
    width: auto;
    height: auto;
    background: none !important;
    box-shadow: none !important;
    margin: 0;
    padding: 0;
    border: none;

    // 移除连接线
    &::after {
      content: none !important;
    }

    .chat-box-icon {
      display: block;
      width: auto;
      height: auto;
      background: none;

      img {
        animation: rotate 20s linear infinite;
      }
    }
  }
}

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.sidebar-avator {
  width: 66px;
  height: 66px;
  margin-bottom: 16px;
  border-radius: 20px;

  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-station-pick {
  width: 66px;
  height: auto;
  min-height: 0;
  padding: 0;
  margin-bottom: 16px;
  background: transparent !important;
  box-shadow: none !important;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-station-pick:deep(.v-btn__content) {
  padding: 0;
}

.sidebar-station-pick :deep(.v-btn__overlay),
.sidebar-station-pick :deep(.v-btn__underlay) {
  opacity: 0 !important;
  display: none !important;
}

.sidebar-station-pick:hover,
.sidebar-station-pick:active,
.sidebar-station-pick:focus,
.sidebar-station-pick:focus-visible {
  background: transparent !important;
  box-shadow: none !important;
  outline: none !important;
}

.sidebar-station-pick__img {
  width: 66px;
  height: auto;
  display: block;
  object-fit: contain;
  /* 图2效果：更明显的外发光阴影（蓝 + 粉） */
  filter: drop-shadow(0 10px 22px rgba(38, 85, 241, 0.30)) drop-shadow(0 4px 14px rgba(241, 91, 255, 0.20));
  cursor: pointer;
}

.sidebar-setting {
  width: 66px;
  height: 66px;
  border-radius: 20px;
  background: #fff;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.switch-btn-txt {
  font-size: 10px;
  white-space: nowrap;
  margin-top: 4px;
}
</style>
